import { useEffect } from 'react';

/**
 * Hook para limpiar cualquier estado residual de modales
 * Previene problemas de bloqueo de página después de cerrar modales
 * Versión mejorada que evita errores de removeChild
 */
export const useModalCleanup = () => {
  useEffect(() => {
    // Función para limpiar modales residuales usando solo CSS
    const cleanupModals = () => {
      try {
        // Remover clase modal-open del body
        document.body.classList.remove('modal-open');
        
        // En lugar de remover elementos, ocultarlos con CSS
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => {
          try {
            if (backdrop) {
              backdrop.style.display = 'none';
              backdrop.style.opacity = '0';
              backdrop.style.visibility = 'hidden';
              backdrop.style.pointerEvents = 'none';
              backdrop.style.zIndex = '-1';
              backdrop.classList.remove('show');
              backdrop.classList.add('fade');
            }
          } catch (error) {
            // Silenciosamente ignorar errores
          }
        });
        
        // Ocultar modales no activas
        const modals = document.querySelectorAll('.modal:not(.show)');
        modals.forEach(modal => {
          try {
            if (modal) {
              modal.style.display = 'none';
              modal.style.opacity = '0';
              modal.style.visibility = 'hidden';
              modal.style.pointerEvents = 'none';
              modal.style.zIndex = '-1';
            }
          } catch (error) {
            // Silenciosamente ignorar errores
          }
        });
        
        // Restaurar scroll del body
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.style.marginRight = '';
      } catch (error) {
        // Solo log en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error in modal cleanup:', error);
        }
      }
    };

    // Listener para errores de DOM que puedan afectar las modales
    const handleDOMError = (event) => {
      if (event.error && event.error.message && 
          (event.error.message.includes('removeChild') || 
           event.error.message.includes('Node.removeChild'))) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('DOM error detected, cleaning up modals with CSS');
        }
        setTimeout(cleanupModals, 100);
      }
    };

    window.addEventListener('error', handleDOMError);

    // Limpiar al desmontar el componente
    return () => {
      window.removeEventListener('error', handleDOMError);
      cleanupModals();
    };
  }, []);

  // Función para limpiar manualmente usando solo CSS (sin remover elementos del DOM)
  const cleanupModal = () => {
    // Usar múltiples requestAnimationFrame para asegurar que React termine completamente
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          try {
            // Limpiar clase modal-open
            document.body.classList.remove('modal-open');
            
            // Ocultar backdrops con CSS en lugar de removerlos
            const backdrops = document.querySelectorAll('.modal-backdrop');
            backdrops.forEach(backdrop => {
              try {
                if (backdrop) {
                  backdrop.style.display = 'none';
                  backdrop.style.opacity = '0';
                  backdrop.style.visibility = 'hidden';
                  backdrop.style.pointerEvents = 'none';
                  backdrop.style.zIndex = '-1';
                  backdrop.classList.remove('show');
                }
              } catch (error) {
                // Silenciosamente ignorar errores
              }
            });
            
            // Restaurar estilos del body
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
            document.body.style.marginRight = '';
            
            // Ocultar modales no activas con CSS
            const hiddenModals = document.querySelectorAll('.modal:not(.show)');
            hiddenModals.forEach(modal => {
              try {
                if (modal) {
                  modal.style.display = 'none';
                  modal.style.opacity = '0';
                  modal.style.visibility = 'hidden';
                  modal.style.pointerEvents = 'none';
                  modal.style.zIndex = '-1';
                }
              } catch (error) {
                // Silenciosamente ignorar errores
              }
            });
            
          } catch (error) {
            // Solo log en desarrollo
            if (process.env.NODE_ENV === 'development') {
              console.warn('Error in manual modal cleanup:', error);
            }
          }
        }, 200);
      });
    });
  };

  return { cleanupModal };
};

export default useModalCleanup;